/* eslint-disable */
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { logOutUser, generateToastError } from '../../../utils';
import { queryKeys } from '../queryKeys';

const httpStatuses = { unauthorized: 401, error: 500 };

/**
 * Hook to get all help articles with optional filters
 * @param {Object} filters - { category, search }
 * @param {string} userToken - Authentication token
 * @param {function} navigate - Navigation function
 * @param {function} toast - Toast notification function
 */
export const useQueryGetHelpArticles = (filters = {}, userToken, navigate, toast) => {
  const queryKey = [queryKeys.getHelpArticles, filters];

  const queryFn = ({ queryKey }) => {
    const params = queryKey[1];
    return axios.get(`${process.env.REACT_APP_SERVER_URL}/api/help/articles`, {
      headers: { Authorization: `Bearer ${userToken}` },
      params,
    });
  };

  return useQuery(queryKey, queryFn, {
    enabled: !!userToken,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

/**
 * Hook to get a single help article by slug
 * @param {string} slug - Article slug or ID
 * @param {string} userToken - Authentication token
 * @param {function} navigate - Navigation function
 * @param {function} toast - Toast notification function
 */
export const useQueryGetHelpArticle = (slug, userToken, navigate, toast) => {
  const queryKey = [queryKeys.getHelpArticle, slug];

  const queryFn = ({ queryKey }) => {
    const articleSlug = queryKey[1];
    return axios.get(`${process.env.REACT_APP_SERVER_URL}/api/help/articles/${articleSlug}`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  };

  return useQuery(queryKey, queryFn, {
    enabled: !!userToken && !!slug,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      if (e?.response?.status !== 404) {
        generateToastError(toast, e);
      }
    },
  });
};

/**
 * Hook to get all help categories
 * @param {string} userToken - Authentication token
 * @param {function} navigate - Navigation function
 * @param {function} toast - Toast notification function
 */
export const useQueryGetHelpCategories = (userToken, navigate, toast) => {
  const queryKey = [queryKeys.getHelpCategories];

  const queryFn = () => {
    return axios.get(`${process.env.REACT_APP_SERVER_URL}/api/help/categories`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  };

  return useQuery(queryKey, queryFn, {
    enabled: !!userToken,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 15 * 60 * 1000, // 15 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

/**
 * Hook to get popular help articles
 * @param {number} limit - Number of articles to fetch
 * @param {string} userToken - Authentication token
 * @param {function} navigate - Navigation function
 * @param {function} toast - Toast notification function
 */
export const useQueryGetPopularArticles = (limit = 5, userToken, navigate, toast) => {
  const queryKey = [queryKeys.getPopularHelpArticles, limit];

  const queryFn = ({ queryKey }) => {
    const articleLimit = queryKey[1];
    return axios.get(`${process.env.REACT_APP_SERVER_URL}/api/help/popular`, {
      headers: { Authorization: `Bearer ${userToken}` },
      params: { limit: articleLimit },
    });
  };

  return useQuery(queryKey, queryFn, {
    enabled: !!userToken,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

/**
 * Hook to mark an article as helpful
 * @param {string} userToken - Authentication token
 * @param {function} toast - Toast notification function
 * @param {function} navigate - Navigation function
 */
export const useMutationMarkArticleHelpful = (userToken, toast, navigate) => {
  const queryClient = useQueryClient();

  return useMutation(
    (articleId) =>
      axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/help/articles/${articleId}/helpful`,
        {},
        {
          headers: { Authorization: `Bearer ${userToken}` },
        }
      ),
    {
      onSuccess: (success, articleId) => {
        toast.success(success?.data?.message || 'Thank you for your feedback!', {
          duration: 2000,
        });
        // Invalidate article query to refresh data
        queryClient.invalidateQueries([queryKeys.getHelpArticle, articleId]);
      },
      onError: (error) => {
        if (error?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, error);
      },
    }
  );
};

/**
 * Hook to create a new article (Admin only)
 * @param {string} userToken - Authentication token
 * @param {function} toast - Toast notification function
 * @param {function} navigate - Navigation function
 * @param {function} onSuccess - Success callback
 */
export const useMutationCreateArticle = (userToken, toast, navigate, onSuccess) => {
  const queryClient = useQueryClient();

  return useMutation(
    (articleData) =>
      axios.post(`${process.env.REACT_APP_SERVER_URL}/api/help/articles`, articleData, {
        headers: { Authorization: `Bearer ${userToken}` },
      }),
    {
      onSuccess: (success) => {
        toast.success(success?.data?.message || 'Article created successfully', {
          duration: 3000,
        });
        queryClient.invalidateQueries([queryKeys.getHelpArticles]);
        queryClient.invalidateQueries([queryKeys.getHelpCategories]);
        if (onSuccess) onSuccess(success.data.data);
      },
      onError: (error) => {
        if (error?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, error);
      },
    }
  );
};

/**
 * Hook to update an article (Admin only)
 * @param {string} userToken - Authentication token
 * @param {function} toast - Toast notification function
 * @param {function} navigate - Navigation function
 * @param {function} onSuccess - Success callback
 */
export const useMutationUpdateArticle = (userToken, toast, navigate, onSuccess) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }) =>
      axios.put(`${process.env.REACT_APP_SERVER_URL}/api/help/articles/${id}`, data, {
        headers: { Authorization: `Bearer ${userToken}` },
      }),
    {
      onSuccess: (success, variables) => {
        toast.success(success?.data?.message || 'Article updated successfully', {
          duration: 3000,
        });
        queryClient.invalidateQueries([queryKeys.getHelpArticles]);
        queryClient.invalidateQueries([queryKeys.getHelpArticle, variables.id]);
        queryClient.invalidateQueries([queryKeys.getHelpCategories]);
        if (onSuccess) onSuccess(success.data.data);
      },
      onError: (error) => {
        if (error?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, error);
      },
    }
  );
};

/**
 * Hook to delete an article (Admin only)
 * @param {string} userToken - Authentication token
 * @param {function} toast - Toast notification function
 * @param {function} navigate - Navigation function
 * @param {function} onSuccess - Success callback
 */
export const useMutationDeleteArticle = (userToken, toast, navigate, onSuccess) => {
  const queryClient = useQueryClient();

  return useMutation(
    (articleId) =>
      axios.delete(`${process.env.REACT_APP_SERVER_URL}/api/help/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${userToken}` },
      }),
    {
      onSuccess: (success) => {
        toast.success(success?.data?.message || 'Article deleted successfully', {
          duration: 3000,
        });
        queryClient.invalidateQueries([queryKeys.getHelpArticles]);
        queryClient.invalidateQueries([queryKeys.getHelpCategories]);
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        if (error?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, error);
      },
    }
  );
};
